import { useEffect, useState, useCallback } from "react";
import { Copy, RefreshCw, Eye, EyeOff, Plus, CheckCircle2, Clock, AlertCircle, Trash2, Mail } from "lucide-react";
import { useMerchant } from "../../../../hooks/useMerchantEngageV1";
import { epicengageSupabasev1 } from "../../../../integrations/supabase/client";
import type { DatabaseEpicEngagev1 } from "../../../../integrations/supabase/epicengagev1types";
import { Button } from "../../../ui/button";
import { Input } from "../../../ui/input";
import { Label } from "../../../ui/label";
import { Badge } from "../../../ui/badge";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../../../ui/card";
import { Slider } from "../../../ui/slider";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../../../ui/tabs";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../../ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../../../ui/dialog";
import { useToast } from "../../../../hooks/use-toast";
import { OutletsManager } from "../components/settings/OutletsManager";
import {  apiUrl, clientheaderconfig  } from '../../../Helpers/Config';
import axios from "axios";
import cookie from "react-cookies";

const SUPABASE_URL = process.env.REACT_APP_SUPABASE_ENGAGEV1_URL as string;

const BUSINESS_TYPES = ["F&B", "Clinic", "Salon", "Tuition Centre", "Retail", "Other"];
const REGIONS = ["Singapore", "Malaysia", "Global"];
const TONES = ["Friendly & Casual", "Professional", "Excited & Playful", "Formal"];

const Settings = () => {
  const { merchant, refresh } = useMerchant();
  const { toast } = useToast();

  // Section 1: profile
  const [name, setName] = useState("");
  const [businessType, setBusinessType] = useState("");
  const [region, setRegion] = useState("Singapore");
  const [savingProfile, setSavingProfile] = useState(false);

  // Section 2: senders
  type Identity = { id: string; identity_type: "email" | "domain"; value: string; verification_status: string; is_default: boolean; last_checked_at: string | null };

  type outelts = {_id:string, value:string, label:string, outletsupabaseEngageV1OutletID:string, outletslug:string}

  const [identities, setIdentities] = useState<Identity[]>([]);
  const [loadingIdentities, setLoadingIdentities] = useState(false);
  const [refreshingId, setRefreshingId] = useState<string | null>(null);
  const [addEmailOpen, setAddEmailOpen] = useState(false);
  const [addDomainOpen, setAddDomainOpen] = useState(false);
  const [newEmail, setNewEmail] = useState("");
  const [newDomain, setNewDomain] = useState("");
  const [instructionsType, setInstructionsType] = useState<"email" | "domain" | null>(null);

  // Section 3: brand tone
  const [brandTone, setBrandTone] = useState("Friendly & Casual");
  const [savingTone, setSavingTone] = useState(false);

  // Section 4: email prefs
  const [maxDiscount, setMaxDiscount] = useState(20);
  const [nameFallback, setNameFallback] = useState("there");
  const [savingEmailPrefs, setSavingEmailPrefs] = useState(false);

  // Section 5: webhook
  const [showSecret, setShowSecret] = useState(false);
  const [rotating, setRotating] = useState(false);
  const [secret, setSecret] = useState<string | null>(null);

  const [outletList, setOutletList] = useState<outelts[]>([]);

  useEffect(() => {
    loadOutlet()
  }, [])
  useEffect(() => {
    if (!merchant) return;
    setName(merchant.name ?? "");
    setBusinessType(merchant.business_type ?? "");
    setRegion(merchant.region ?? "Singapore");
    setBrandTone(merchant.brand_tone ?? "Friendly & Casual");
    // sender identities loaded by separate effect
    // Pull max_discount_pct (not in merchant context type, fetch directly)
    epicengageSupabasev1
      .from("merchants")
      .select("max_discount_pct, default_first_name_fallback")
      .eq("id", merchant.id)
      .maybeSingle()
      .then(({ data }) => {
        if (data?.max_discount_pct != null) setMaxDiscount(data.max_discount_pct);
        if (data && "default_first_name_fallback" in data && data.default_first_name_fallback) {
          setNameFallback(data.default_first_name_fallback as string);
        }
      });
  }, [merchant]);

  type MerchantUpdate = DatabaseEpicEngagev1["public"]["Tables"]["merchants"]["Update"];
  const updateMerchant = async (patch: MerchantUpdate) => {
    if (!merchant) return false;
    const { error } = await epicengageSupabasev1.from("merchants").update(patch).eq("id", merchant.id);
    if (error) {
      toast({ title: "Save failed", description: error.message, variant: "destructive" });
      return false;
    }
    await refresh();
    return true;
  };
  const loadOutlet = async () => {
    var companyID = cookie.load("companyID");
    var urlShringTxt = apiUrl + "outlet/dropdown?company_id=" + companyID;
        axios
          .get(urlShringTxt, clientheaderconfig)
          .then((res) => {
            if (res.data.status === "ok") {
              const result =  res.data.result;
              setOutletList(result)
            }
          })
          .catch();
  }
  const saveProfile = async () => {
    if (!name.trim()) {
      toast({ title: "Business name required", variant: "destructive" });
      return;
    }
    setSavingProfile(true);
    const ok = await updateMerchant({ name: name.trim(), business_type: businessType || null, region });
    setSavingProfile(false);
    if (ok) toast({ title: "Profile saved" });
  };

  const saveTone = async () => {
    setSavingTone(true);
    const ok = await updateMerchant({ brand_tone: brandTone });
    setSavingTone(false);
    if (ok) toast({ title: "Brand tone saved" });
  };

  const saveEmailPrefs = async () => {
    setSavingEmailPrefs(true);
    const fallback = (nameFallback.trim() || "there").slice(0, 40);
    const ok = await updateMerchant({
      max_discount_pct: maxDiscount,
      default_first_name_fallback: fallback,
    });
    setSavingEmailPrefs(false);
    if (ok) {
      setNameFallback(fallback);
      toast({ title: "Email settings saved" });
    }
  };

  const loadIdentities = useCallback(async () => {
    if (!merchant) return;
    setLoadingIdentities(true);
    const { data } = await epicengageSupabasev1
      .from("sender_identities")
      .select("id, identity_type, value, verification_status, is_default, last_checked_at")
      .eq("merchant_id", merchant.id)
      .order("identity_type", { ascending: true })
      .order("created_at", { ascending: true });
    setIdentities((data ?? []) as Identity[]);
    setLoadingIdentities(false);
  }, [merchant]);

  useEffect(() => { loadIdentities(); }, [loadIdentities]);

  const callVerify = async (payload: Record<string, unknown>) => {
    const { data, error } = await epicengageSupabasev1.functions.invoke("verify-sender", { body: payload });
    if (error) throw new Error(error.message);
    if ((data as { error?: string })?.error) throw new Error((data as { error: string }).error);
    return data;
  };

  const addIdentity = async (type: "email" | "domain") => {
    if (!merchant) return;
    const raw = type === "email" ? newEmail.trim().toLowerCase() : newDomain.trim().toLowerCase();
    if (type === "email" && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(raw)) {
      toast({ title: "Invalid email", variant: "destructive" }); return;
    }
    if (type === "domain" && !/^[a-z0-9.-]+\.[a-z]{2,}$/.test(raw)) {
      toast({ title: "Invalid domain", variant: "destructive" }); return;
    }
    const hasDefault = identities.some((i) => i.identity_type === type && i.is_default);
    const { data: inserted, error } = await epicengageSupabasev1
      .from("sender_identities")
      .insert({ merchant_id: merchant.id, identity_type: type, value: raw, is_default: !hasDefault })
      .select()
      .single();
    if (error) {
      toast({ title: "Could not add", description: error.message, variant: "destructive" });
      return;
    }
    if (type === "email") { setAddEmailOpen(false); setNewEmail(""); }
    else { setAddDomainOpen(false); setNewDomain(""); }
    try {
      await callVerify({ action: "request", identity_id: inserted.id });
      toast({ title: type === "email" ? "Verification email sent" : "Domain verification started", description: type === "email" ? "Check the inbox and click the AWS link." : "Add the DKIM records shown in AWS SES." });
    } catch (e) {
      toast({ title: "Verification request failed", description: String((e as Error).message), variant: "destructive" });
    }
    loadIdentities();
  };

  const refreshIdentity = async (id: string) => {
    setRefreshingId(id);
    try {
      await callVerify({ action: "refresh", identity_id: id });
      await loadIdentities();
    } catch (e) {
      toast({ title: "Refresh failed", description: String((e as Error).message), variant: "destructive" });
    } finally { setRefreshingId(null); }
  };

  const resendVerification = async (id: string) => {
    try {
      await callVerify({ action: "request", identity_id: id });
      toast({ title: "Verification re-sent" });
      loadIdentities();
    } catch (e) {
      toast({ title: "Could not resend", description: String((e as Error).message), variant: "destructive" });
    }
  };

  const deleteIdentity = async (id: string) => {
    const { error } = await epicengageSupabasev1.from("sender_identities").delete().eq("id", id);
    if (error) { toast({ title: "Delete failed", description: error.message, variant: "destructive" }); return; }
    loadIdentities();
  };

  const setDefaultIdentity = async (row: Identity) => {
    if (!merchant) return;
    if (row.verification_status !== "verified") {
      toast({ title: "Verify first", description: "Only verified identities can be the default sender.", variant: "destructive" });
      return;
    }
    // Clear other defaults of same type, then set this one
    await epicengageSupabasev1.from("sender_identities").update({ is_default: false }).eq("merchant_id", merchant.id).eq("identity_type", row.identity_type);
    await epicengageSupabasev1.from("sender_identities").update({ is_default: true }).eq("id", row.id);
    if (row.identity_type === "email") {
      await updateMerchant({ sender_email: row.value, ses_verified: true });
    } else {
      await updateMerchant({ sender_domain: row.value, ses_verified: true });
    }
    loadIdentities();
  };

  const loadSecret = async () => {
    if (!merchant) return;
    const { data } = await epicengageSupabasev1
      .from("merchants")
      .select("webhook_secret")
      .eq("id", merchant.id)
      .maybeSingle();
    setSecret(data?.webhook_secret ?? null);
    setShowSecret(true);
  };

  const rotate = async () => {
    if (!merchant) return;
    setRotating(true);
    const newSecret = crypto.randomUUID();
    const { error } = await epicengageSupabasev1
      .from("merchants")
      .update({ webhook_secret: newSecret })
      .eq("id", merchant.id);
    setRotating(false);
    if (error) {
      toast({ title: "Could not rotate secret", description: error.message, variant: "destructive" });
      return;
    }
    setSecret(newSecret);
    setShowSecret(true);

    const webhookUrl = merchant ? `${SUPABASE_URL}/functions/v1/customer-sync/${merchant.id}` : "";
    const postObject = {
      companysupabaseEngageV1WebhookURL:`${webhookUrl}`,
      companysupabaseEngageV1WebhookSecret:newSecret
    }
    axios
      .put(`${apiUrl}company/updateSetting`, postObject, clientheaderconfig)
      .then((res) => {
        if (res.data.status === "ok") {
        }
        console.log(res.data, "res.datares.datares.data");
      })
      .catch((e) => {
      });

    toast({ title: "Webhook secret rotated", description: "Update your sender system with the new secret." });
  };

  const copy = (text: string, label: string) => {
    navigator.clipboard.writeText(text);
    toast({ title: `${label} copied` });
  };

  const webhookUrl = merchant ? `${SUPABASE_URL}/functions/v1/customer-sync/${merchant.id}` : "";

  const samplePayload = `{
  "source": "EpicReview",
  "customers": [
    {
      "external_customer_id": "cus_123",
      "name": "Jane Doe",
      "email": "jane@example.com",
      "status": "active",
      "joined_at": "2026-01-15T10:00:00Z",
      "last_activity_at": "2026-04-12T18:30:00Z"
    }
  ]
}`;

  const StatusBadge = ({ verified }: { verified: boolean }) =>
    verified ? (
      <Badge className="gap-1"><CheckCircle2 className="h-3 w-3" /> Verified</Badge>
    ) : (
      <Badge variant="secondary" className="gap-1"><Clock className="h-3 w-3" /> Pending</Badge>
    );

  const IdentityStatusBadge = ({ status }: { status: string }) => {
    if (status === "verified") return <Badge className="gap-1"><CheckCircle2 className="h-3 w-3" /> Verified</Badge>;
    if (status === "failed") return <Badge variant="destructive" className="gap-1"><AlertCircle className="h-3 w-3" /> Failed</Badge>;
    if (status === "temporary_failure") return <Badge variant="destructive" className="gap-1"><AlertCircle className="h-3 w-3" /> Retry</Badge>;
    if (status === "not_started") return <Badge variant="outline" className="gap-1"><Clock className="h-3 w-3" /> Not started</Badge>;
    return <Badge variant="secondary" className="gap-1"><Clock className="h-3 w-3" /> Pending</Badge>;
  };

  return (
    <div>
      <div className="px-10 py-10 space-y-6">
        <header>
          <h1 className="text-2xl font-semibold tracking-tight">Settings</h1>
          <p className="text-sm text-muted-foreground mt-1">
            Manage your business profile, sender identities, and integrations
          </p>
        </header>

        {/* Section 1: Business Profile */}
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Business profile</CardTitle>
            <CardDescription>How your business is identified across EpicEngage</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="biz-name">Business name</Label>
                <Input id="biz-name" value={name} onChange={(e) => setName(e.target.value)} className="mt-1.5" />
              </div>
              <div>
                <Label>Business type</Label>
                <Select value={businessType} onValueChange={setBusinessType}>
                  <SelectTrigger className="mt-1.5"><SelectValue placeholder="Select type" /></SelectTrigger>
                  <SelectContent>
                    {BUSINESS_TYPES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Region</Label>
                <Select value={region} onValueChange={setRegion}>
                  <SelectTrigger className="mt-1.5"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {REGIONS.map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Brand tone</Label>
                <Select value={brandTone} onValueChange={setBrandTone}>
                  <SelectTrigger className="mt-1.5"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {TONES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="flex justify-end">
              <Button onClick={saveProfile} disabled={savingProfile}>
                {savingProfile ? "Saving…" : "Save"}
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Section 2: Sender Identities */}
        <Card>
          <CardHeader className="flex flex-row items-start justify-between gap-2">
            <div>
              <CardTitle className="text-base">Sender identities</CardTitle>
              <CardDescription>Verified addresses and domains you can send from</CardDescription>
            </div>
            <Button variant="ghost" size="sm" onClick={loadIdentities} disabled={loadingIdentities}>
              <RefreshCw className={`h-4 w-4 ${loadingIdentities ? "animate-spin" : ""}`} /> Refresh
            </Button>
          </CardHeader>
          <CardContent>
            <Tabs defaultValue="emails">
              <TabsList>
                <TabsTrigger value="emails">Email Addresses</TabsTrigger>
                <TabsTrigger value="domains">Domains</TabsTrigger>
              </TabsList>

              {(["email", "domain"] as const).map((type) => {
                const list = identities.filter((i) => i.identity_type === type);
                return (
                  <TabsContent key={type} value={type === "email" ? "emails" : "domains"} className="mt-4 space-y-2">
                    {list.length === 0 ? (
                      <p className="text-sm text-muted-foreground">No sender {type === "email" ? "emails" : "domains"} yet.</p>
                    ) : (
                      list.map((row) => (
                        <div key={row.id} className="flex items-center justify-between rounded-md border border-border p-3 gap-2">
                          <div className="flex items-center gap-2 min-w-0">
                            <span className="text-sm font-mono truncate">{row.value}</span>
                            {row.is_default && <Badge variant="outline" className="text-xs">Default</Badge>}
                          </div>
                          <div className="flex items-center gap-1 shrink-0">
                            <IdentityStatusBadge status={row.verification_status} />
                            <Button variant="ghost" size="sm" onClick={() => refreshIdentity(row.id)} disabled={refreshingId === row.id} title="Re-check status">
                              <RefreshCw className={`h-3.5 w-3.5 ${refreshingId === row.id ? "animate-spin" : ""}`} />
                            </Button>
                            {row.verification_status !== "verified" && (
                              <Button variant="ghost" size="sm" onClick={() => resendVerification(row.id)} title={type === "email" ? "Resend verification email" : "Re-trigger DKIM"}>
                                <Mail className="h-3.5 w-3.5" />
                              </Button>
                            )}
                            {!row.is_default && row.verification_status === "verified" && (
                              <Button variant="ghost" size="sm" onClick={() => setDefaultIdentity(row)} title="Set as default">
                                <CheckCircle2 className="h-3.5 w-3.5" />
                              </Button>
                            )}
                            {!row.is_default && (
                              <Button variant="ghost" size="sm" onClick={() => deleteIdentity(row.id)} title="Remove">
                                <Trash2 className="h-3.5 w-3.5" />
                              </Button>
                            )}
                          </div>
                        </div>
                      ))
                    )}
                    <Button variant="outline" size="sm" onClick={() => (type === "email" ? setAddEmailOpen(true) : setAddDomainOpen(true))}>
                      <Plus className="h-4 w-4" /> Add {type === "email" ? "Email" : "Domain"}
                    </Button>
                    {type === "email" && (
                      <p className="text-xs text-muted-foreground pt-2">
                        AWS sends a verification email to each address. Click the link in that email, then press the refresh icon here to update the status.{" "}
                        <button type="button" onClick={() => setInstructionsType("email")} className="text-primary underline underline-offset-2 hover:opacity-80">
                          View step-by-step instructions
                        </button>
                      </p>
                    )}
                    {type === "domain" && (
                      <p className="text-xs text-muted-foreground pt-2">
                        Domains are verified via DKIM DNS records in your domain provider. Add the records shown in the AWS SES console, then refresh.{" "}
                        <button type="button" onClick={() => setInstructionsType("domain")} className="text-primary underline underline-offset-2 hover:opacity-80">
                          View step-by-step instructions
                        </button>
                      </p>
                    )}
                  </TabsContent>
                );
              })}
            </Tabs>
          </CardContent>
        </Card>

        {/* Outlets */}
        <OutletsManager />

        {/* Section 3: Brand Tone */}
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Brand tone</CardTitle>
            <CardDescription>Used by AI when generating email drafts</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <Select value={brandTone} onValueChange={setBrandTone}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                {TONES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
              </SelectContent>
            </Select>
            <div className="flex justify-end">
              <Button onClick={saveTone} disabled={savingTone} variant="outline">
                {savingTone ? "Saving…" : "Save Tone"}
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Section 4: Email Preferences */}
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Email preferences</CardTitle>
            <CardDescription>Maximum discount AI is allowed to offer in generated campaigns</CardDescription>
          </CardHeader>
          <CardContent className="space-y-5">
            <div>
              <div className="flex items-center justify-between mb-3">
                <Label>Max discount</Label>
                <span className="text-sm font-medium">{maxDiscount}%</span>
              </div>
              <Slider
                value={[maxDiscount]}
                onValueChange={(v) => setMaxDiscount(v[0])}
                min={0}
                max={50}
                step={1}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="name-fallback">First name fallback</Label>
              <Input
                id="name-fallback"
                value={nameFallback}
                onChange={(e) => setNameFallback(e.target.value)}
                placeholder="there"
                maxLength={40}
              />
              <p className="text-xs text-muted-foreground">
                Used for placeholders like <code>{"{{first_name}}"}</code> when a customer has no name on file. Example: <em>Hi {nameFallback.trim() || "there"},</em>
              </p>
            </div>
            <div className="flex justify-end">
              <Button onClick={saveEmailPrefs} disabled={savingEmailPrefs}>
                {savingEmailPrefs ? "Saving…" : "Save Email Settings"}
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Section 5: Customer Sync */}
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Customer sync</CardTitle>
            <CardDescription>
              Send POST requests to sync customers from EpicReview or EpicCommerce
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-5">
            <div>
              <Label className="text-xs uppercase tracking-wide text-muted-foreground">Webhook URL</Label>
              <div className="flex gap-2 mt-1.5">
                <Input value={webhookUrl} readOnly className="font-mono text-xs" />
                <Button variant="outline" size="icon" onClick={() => copy(webhookUrl, "URL")} aria-label="Copy URL">
                  <Copy className="h-4 w-4" />
                </Button>
              </div>
            </div>

            <div>
              <Label className="text-xs uppercase tracking-wide text-muted-foreground">Webhook secret</Label>
              <div className="flex gap-2 mt-1.5">
                <Input
                  value={showSecret && secret ? secret : "••••••••••••••••••••••••••••••••"}
                  readOnly
                  className="font-mono text-xs"
                />
                {!showSecret ? (
                  <Button variant="outline" size="icon" onClick={loadSecret} aria-label="Reveal secret">
                    <Eye className="h-4 w-4" />
                  </Button>
                ) : (
                  <Button variant="outline" size="icon" onClick={() => { setShowSecret(false); setSecret(null); }} aria-label="Hide secret">
                    <EyeOff className="h-4 w-4" />
                  </Button>
                )}
                <Button variant="outline" size="icon" onClick={() => secret && copy(secret, "Secret")} disabled={!secret} aria-label="Copy secret">
                  <Copy className="h-4 w-4" />
                </Button>
                <Button variant="outline" onClick={rotate} disabled={rotating}>
                  <RefreshCw className="h-4 w-4" /> {rotating ? "Rotating…" : "Regenerate"}
                </Button>
              </div>
              <p className="text-xs text-muted-foreground mt-2">
                Sign each request body with HMAC-SHA256 using this secret and send as{" "}
                <code className="text-foreground">X-Webhook-Signature</code> header.
              </p>
            </div>

            <div>
              <Label className="text-xs uppercase tracking-wide text-muted-foreground">Expected payload</Label>
              <pre className="mt-1.5 rounded-md bg-muted p-4 text-xs overflow-x-auto font-mono border border-border">{samplePayload}</pre>
            </div>

            <div>
              <Label className="text-xs uppercase tracking-wide text-muted-foreground">Response format</Label>
              <pre className="mt-1.5 rounded-md bg-muted p-4 text-xs overflow-x-auto font-mono border border-border">{`{ "ok": true, "upserted": 1, "skipped": 0 }`}</pre>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Add Email dialog */}
      <Dialog open={addEmailOpen} onOpenChange={setAddEmailOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>Add sender email</DialogTitle></DialogHeader>
          <div className="space-y-2">
            <Label htmlFor="new-email">Email address</Label>
            <Input id="new-email" type="email" value={newEmail} onChange={(e) => setNewEmail(e.target.value)} placeholder="hello@yourbrand.com" />
            <p className="text-xs text-muted-foreground">We'll email this address a verification link from AWS. Click the link, then come back and press refresh.</p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAddEmailOpen(false)}>Cancel</Button>
            <Button onClick={() => addIdentity("email")}>Add</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add Domain dialog */}
      <Dialog open={addDomainOpen} onOpenChange={setAddDomainOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>Add sender domain</DialogTitle></DialogHeader>
          <div className="space-y-2">
            <Label htmlFor="new-domain">Domain</Label>
            <Input id="new-domain" value={newDomain} onChange={(e) => setNewDomain(e.target.value)} placeholder="yourbrand.com" />
            <p className="text-xs text-muted-foreground">We'll start DKIM verification with AWS. Add the DKIM CNAME records (visible in the AWS SES console) at your DNS provider, then refresh.</p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAddDomainOpen(false)}>Cancel</Button>
            <Button onClick={() => addIdentity("domain")}>Add</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Verification instructions dialog */}
      <Dialog open={instructionsType !== null} onOpenChange={(o) => !o && setInstructionsType(null)}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {instructionsType === "email" ? "How to verify a sender email" : "How to verify a sender domain (DKIM)"}
            </DialogTitle>
          </DialogHeader>

          {instructionsType === "email" && (
            <div className="space-y-4 text-sm">
              <p className="text-muted-foreground">
                Verifying an individual email address is the fastest way to start sending. AWS will email a confirmation link to that inbox.
              </p>
              <ol className="space-y-3 list-decimal pl-5">
                <li>
                  <strong>Add the email</strong> — Click <em>Add Email</em>, type the full address (e.g. <code className="bg-muted px-1 rounded">hello@yourbrand.com</code>), and submit.
                </li>
                <li>
                  <strong>Check the inbox</strong> — Within a minute, AWS sends an email titled <em>"Amazon Web Services – Email Address Verification Request"</em>. Open it (check spam if you don't see it).
                </li>
                <li>
                  <strong>Click the verification link</strong> in that email. You'll see an AWS confirmation page.
                </li>
                <li>
                  <strong>Come back here and press the refresh icon</strong> next to the address. The status will flip from <em>Pending</em> to <em>Verified</em>.
                </li>
                <li>
                  <strong>Set as default</strong> (optional) — Once verified, click the checkmark icon to use this address as your default sender.
                </li>
              </ol>
              <div className="rounded-md border border-amber-200 bg-amber-50 p-3 text-xs text-amber-900">
                <strong>Note:</strong> The verification link expires after 24 hours. If it expires, click the resend (envelope) icon to get a fresh one.
              </div>
            </div>
          )}

          {instructionsType === "domain" && (
            <div className="space-y-4 text-sm">
              <p className="text-muted-foreground">
                Verifying a whole domain lets you send from any address on that domain (e.g. hello@, support@, noreply@). It uses DKIM DNS records.
              </p>
              <ol className="space-y-3 list-decimal pl-5">
                <li>
                  <strong>Add the domain</strong> — Click <em>Add Domain</em>, enter your root domain (e.g. <code className="bg-muted px-1 rounded">yourbrand.com</code>, no <code className="bg-muted px-1 rounded">www</code>), and submit. We register it with AWS SES.
                </li>
                <li>
                  <strong>Open the AWS SES console</strong> in a new tab:{" "}
                  <a
                    href="https://ap-southeast-1.console.aws.amazon.com/ses/home?region=ap-southeast-1#/identities"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-primary underline"
                  >
                    SES Identities (Singapore region)
                  </a>
                  . Sign in if needed and click on the domain you just added.
                </li>
                <li>
                  <strong>Find the DKIM records</strong> — Under the domain detail, expand the <em>DomainKeys Identified Mail (DKIM)</em> section. AWS shows <strong>three CNAME records</strong>, each looking like:
                  <pre className="bg-muted p-2 rounded mt-1 text-xs overflow-x-auto">
{`Name:  abc123._domainkey.yourbrand.com
Type:  CNAME
Value: abc123.dkim.amazonses.com`}
                  </pre>
                </li>
                <li>
                  <strong>Add all 3 CNAME records</strong> to your DNS provider (GoDaddy, Cloudflare, Namecheap, Route 53, etc.). When pasting:
                  <ul className="list-disc pl-5 mt-1 space-y-1">
                    <li>Most providers auto-append your domain — paste just <code className="bg-muted px-1 rounded">abc123._domainkey</code> (without <code className="bg-muted px-1 rounded">.yourbrand.com</code>) into the <em>Name</em> field.</li>
                    <li>Paste the full <code className="bg-muted px-1 rounded">....dkim.amazonses.com</code> string into the <em>Value / Target</em> field.</li>
                    <li>Leave TTL at the default (or 1 hour).</li>
                  </ul>
                </li>
                <li>
                  <strong>Wait for DNS propagation</strong> — usually 5–30 minutes, sometimes up to 72 hours.
                </li>
                <li>
                  <strong>Press the refresh icon</strong> next to the domain here. Once AWS sees all 3 records, the status flips to <em>Verified</em>.
                </li>
                <li>
                  <strong>Set as default</strong> (optional) — Click the checkmark to use this domain for outgoing email.
                </li>
              </ol>
              <div className="rounded-md border border-amber-200 bg-amber-50 p-3 text-xs text-amber-900">
                <strong>Tip:</strong> Don't delete the CNAME records after verification — AWS keeps re-checking them. Removing them will revoke the verification.
              </div>
            </div>
          )}

          <DialogFooter>
            <Button onClick={() => setInstructionsType(null)}>Got it</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Settings;
